import React, { useState, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface ThumbnailSelectorProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string;
  onConfirm: (selection: { page: number; x: number; y: number; width: number; height: number }) => void;
}

const ThumbnailSelector: React.FC<ThumbnailSelectorProps> = ({ open, onClose, pdfUrl, onConfirm }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection state
  const [isSelecting, setIsSelecting] = useState(false);
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(null);
  const [currentPos, setCurrentPos] = useState<{ x: number; y: number } | null>(null);
  const [selection, setSelection] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: any) => {
    console.error('PDF load error:', error);
    setError('Erreur lors du chargement du PDF');
    setLoading(false);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!pdfContainerRef.current) return;

    const rect = pdfContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsSelecting(true);
    setStartPos({ x, y });
    setCurrentPos({ x, y });
    setSelection(null);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !startPos || !pdfContainerRef.current) return;

    const rect = pdfContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCurrentPos({ x, y });
  };

  const handleMouseUp = () => {
    if (!isSelecting || !startPos || !currentPos || !pdfContainerRef.current) return;

    setIsSelecting(false);

    // Get the actual PDF canvas dimensions
    const canvas = pdfContainerRef.current.querySelector('canvas');
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    const containerRect = pdfContainerRef.current.getBoundingClientRect();

    // Calculate positions relative to the canvas
    const canvasOffsetX = canvasRect.left - containerRect.left;
    const canvasOffsetY = canvasRect.top - containerRect.top;

    // Calculate selection rectangle relative to canvas
    const x = Math.min(startPos.x, currentPos.x) - canvasOffsetX;
    const y = Math.min(startPos.y, currentPos.y) - canvasOffsetY;
    const width = Math.abs(currentPos.x - startPos.x);
    const height = Math.abs(currentPos.y - startPos.y);

    // Convert to percentages relative to the actual canvas dimensions
    const xPercent = (x / canvasRect.width) * 100;
    const yPercent = (y / canvasRect.height) * 100;
    const widthPercent = (width / canvasRect.width) * 100;
    const heightPercent = (height / canvasRect.height) * 100;

    setSelection({
      x: xPercent,
      y: yPercent,
      width: widthPercent,
      height: heightPercent
    });
  };

  const handleConfirm = () => {
    if (selection) {
      onConfirm({
        page: pageNumber,
        ...selection
      });
    }
  };

  const renderSelectionBox = () => {
    if (!startPos || !currentPos) return null;

    const x = Math.min(startPos.x, currentPos.x);
    const y = Math.min(startPos.y, currentPos.y);
    const width = Math.abs(currentPos.x - startPos.x);
    const height = Math.abs(currentPos.y - startPos.y);

    return (
      <Box
        sx={{
          position: 'absolute',
          left: `${x}px`,
          top: `${y}px`,
          width: `${width}px`,
          height: `${height}px`,
          border: '2px dashed #1976d2',
          backgroundColor: 'rgba(25, 118, 210, 0.1)',
          pointerEvents: 'none',
          zIndex: 10
        }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Sélectionner la zone de miniature
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Cliquez et faites glisser pour sélectionner une zone rectangulaire sur le PDF. Cette zone sera utilisée comme miniature pour le cours.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {selection && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Zone sélectionnée : {Math.round(selection.width)}% × {Math.round(selection.height)}%
          </Alert>
        )}

        {/* PDF Viewer with selection */}
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: '600px',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5'
          }}
        >
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          )}

          <Box
            ref={pdfContainerRef}
            sx={{
              position: 'relative',
              cursor: 'crosshair'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading=""
              error=""
            >
              <Page
                pageNumber={pageNumber}
                scale={1.5}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>

            {(isSelecting || selection) && renderSelectionBox()}
          </Box>
        </Box>

        {/* Page navigation */}
        {numPages && numPages > 1 && (
          <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
              disabled={pageNumber <= 1}
            >
              Page précédente
            </Button>
            <Typography variant="body2">
              Page {pageNumber} sur {numPages}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
              disabled={pageNumber >= numPages}
            >
              Page suivante
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Annuler
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selection}
        >
          Définir comme miniature
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThumbnailSelector;
